/* eslint-disable @typescript-eslint/no-use-before-define */
import { toast } from '@postscript/components';
import { PERMISSIONS } from 'components/account/AccountView/users/constants';
import { AnnouncementType } from 'components/admin/Announcements/utils';
import {
  findActiveUsageCredits,
  formatDollars,
  sumUsageCredits,
  toggleButtonLoadingState,
} from 'components/billing/common/utils';
import { useUsageBilling } from 'components/billing/context/usageBilling';
import {
  useGetInvoicingSettings,
  useGetInvoicingStatus,
  useGetPaymentMethod,
  useGetUsageCredits,
} from 'components/billing/context/useBilling';
import useGetUsageCreditRemaining from 'components/billing/modules/usageCredit/hooks/useGetUsageCreditRemaining';
import { useBanners } from 'components/GlobalBanners/globalBanners';
import useAuth from 'hooks/useAuth';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { BANNER_TYPES, logButtonClickEvent } from 'utils/events';
import {
  FREE_TRIAL_BANNER_ID,
  FREE_USAGE_CREDIT_AND_FREE_TRIAL_BANNER_ID,
  FREE_USAGE_CREDIT_BANNER_ID,
  LONG_DATE_FORMAT,
  PAYMENT_METHOD_BANNER_ID,
  PAYMENT_PROVIDERS,
  RAC_FLOW_BANNER_ID,
  RAC_STATUSES,
  SHOP_FAILED_INVOICE_BANNER_ID,
  SHOP_PAST_DUE_BANNER_ID,
} from '../constants';

export default function BillingBanners(): null {
  const [isInFreeTrial, setIsInFreeTrial] = useState(false);
  const [hasFreeUsageCredit, setHasFreeUsageCredit] = useState(false);
  const { userHasPermission } = useAuth();
  const {
    freeTrial,
    isInitialDataLoaded,
    showPaymentMethodsModal,
    showPayNowModal,
    generateRac,
  } = useUsageBilling();
  const { data: usageCredits } = useGetUsageCredits();
  const activeUsageCredits = findActiveUsageCredits(usageCredits ?? []);
  const estimatedUsageCreditRemaining = useGetUsageCreditRemaining();
  const { addBanner, removeBanner, updateBanner } = useBanners();
  const { push } = useHistory();
  const { data: invoicingStatus } = useGetInvoicingStatus();
  const { numberOfFailedInvoices = 0, isPastDue = false } =
    invoicingStatus ?? {};
  const { data: paymentMethodData } = useGetPaymentMethod();
  const { paymentProvider, card, usBankAccount, recurringApplicationCharge } =
    paymentMethodData ?? {};
  const { data: invoicingSettings } = useGetInvoicingSettings();
  const { manualPayments } = invoicingSettings ?? {};

  useEffect(() => {
    const { amountTotal, amountUsed } = sumUsageCredits(activeUsageCredits);

    setHasFreeUsageCredit(!!usageCredits && amountTotal - amountUsed > 0);

    setIsInFreeTrial(!!freeTrial && !freeTrial.isExpired);
  }, [usageCredits, freeTrial]);

  useEffect(() => {
    if (!isInitialDataLoaded || !userHasPermission(PERMISSIONS.billing_read)) {
      return;
    }

    removeAllBillingBanners();
    displayRequiredBanners();
  }, [
    isInitialDataLoaded,
    paymentProvider,
    card,
    usBankAccount,
    recurringApplicationCharge,
    numberOfFailedInvoices,
    hasFreeUsageCredit,
    isInFreeTrial,
    manualPayments,
  ]);

  useEffect(() => {
    if (!hasFreeUsageCredit) return;
    updateUsageCreditBannerBodyText();
  }, [estimatedUsageCreditRemaining]);

  function removeAllBillingBanners() {
    removeBanner(PAYMENT_METHOD_BANNER_ID);
    removeBanner(RAC_FLOW_BANNER_ID);
    removeBanner(SHOP_PAST_DUE_BANNER_ID);
    removeBanner(SHOP_FAILED_INVOICE_BANNER_ID);
    removeBanner(FREE_TRIAL_BANNER_ID);
    removeBanner(FREE_USAGE_CREDIT_BANNER_ID);
    removeBanner(FREE_USAGE_CREDIT_AND_FREE_TRIAL_BANNER_ID);
  }

  function displayRequiredBanners() {
    if (
      paymentProvider === PAYMENT_PROVIDERS.STRIPE &&
      !card &&
      !usBankAccount &&
      !manualPayments
    ) {
      addPaymentMethodBanner();
    }

    if (
      paymentProvider === PAYMENT_PROVIDERS.SHOPIFY &&
      recurringApplicationCharge?.status !== RAC_STATUSES.ACTIVE
    ) {
      addRacApprovalRequiredBanner();
    }

    if (numberOfFailedInvoices > 0) {
      addUnpaidInvoiceBanner(numberOfFailedInvoices);
    }

    if (hasFreeUsageCredit && isInFreeTrial) {
      return addFreeUsageAndFreeTrialBanner();
    }

    if (hasFreeUsageCredit) {
      return addFreeUsageCreditBanner();
    }

    if (isInFreeTrial) {
      return addFreeTrialBanner();
    }
  }

  function addPaymentMethodBanner() {
    const { amountTotal, amountUsed } = sumUsageCredits(activeUsageCredits);
    const usageCreditPercentUsed = (amountUsed / amountTotal) * 100;
    const freeTrialDaysRemaining = moment(freeTrial?.expiresAt).diff(
      moment(),
      'days',
    );

    // Free Trial Warning Threshold (any of the following):
    // No current free trial
    // 7 or fewer days remaining in free trial
    const hasMetFreeTrialWarningThreshold =
      !isInFreeTrial || freeTrialDaysRemaining <= 7;

    // Free Usage Credit Warning Threshold (any of the following):
    // No unexpired credit remaining
    // 10% or less credit remaining
    const hasMetUsageCreditWarningThreshold =
      !hasFreeUsageCredit || usageCreditPercentUsed >= 90;

    // isWarning makes this a persistent banner with more specific copy
    const isWarning =
      hasMetFreeTrialWarningThreshold && hasMetUsageCreditWarningThreshold;

    let heading = 'Add a payment method';

    if (isWarning && hasFreeUsageCredit) {
      heading = 'Your credit expires soon';
    }

    if (isWarning && isInFreeTrial) {
      heading = 'Your free trial is almost over';
    }

    addBanner({
      id: PAYMENT_METHOD_BANNER_ID,
      eventTrackingOptions: {
        bannerType: BANNER_TYPES.BILLING_PAYMENT_METHOD,
      },
      isDismissable: !isWarning,
      shouldPersistDismiss: !isWarning,
      data: {
        variant: isWarning ? 'warning' : 'guidance',
        heading,
        bodyText:
          'Ensure you can continue sending with no interruptions by adding a card or bank account.',
        primaryAction: {
          text: 'Add Payment Method',
          onClick: (event: React.SyntheticEvent) => {
            logButtonClickEvent(event, {
              banner_type: BANNER_TYPES.BILLING_PAYMENT_METHOD,
            });
            showPaymentMethodsModal();
          },
        },
      },
    });
  }

  function addRacApprovalRequiredBanner() {
    const buttonId = `${RAC_FLOW_BANNER_ID}-button`;
    const buttonText = 'Approve on Shopify';

    addBanner({
      id: RAC_FLOW_BANNER_ID,
      eventTrackingOptions: {
        bannerType: BANNER_TYPES.BILLING_RAC_APPROVAL,
      },
      data: {
        variant: 'warning',
        heading: 'Complete billing setup',
        bodyText: 'Postscript needs your permission to bill you via Shopify.',
        primaryAction: {
          id: buttonId,
          text: buttonText,
          onClick: async (event: React.SyntheticEvent) => {
            logButtonClickEvent(event, {
              banner_type: BANNER_TYPES.BILLING_RAC_APPROVAL,
            });

            const button = document.getElementById(
              buttonId,
            ) as HTMLButtonElement | null;

            try {
              toggleButtonLoadingState({
                button,
                originalText: buttonText,
                isLoading: true,
              });

              const rac = await generateRac();

              if (!rac) {
                toggleButtonLoadingState({
                  button,
                  originalText: buttonText,
                  isLoading: false,
                });

                return toast.error('Please try again.');
              }

              window.location.href = rac.confirmationUrl;
            } catch (error) {
              toggleButtonLoadingState({
                button,
                originalText: buttonText,
                isLoading: false,
              });

              toast.error(error as string);
            }
          },
        },
      },
    });
  }

  function addUnpaidInvoiceBanner(numberOfFailedInvoices: number) {
    // `isPastDue` in this context means "is past due AND blocked from sending"
    // An account can be "past due" but not blocked from sending
    addBanner({
      id: isPastDue ? SHOP_PAST_DUE_BANNER_ID : SHOP_FAILED_INVOICE_BANNER_ID,
      announcementType: AnnouncementType.EMERGENCY,
      eventTrackingOptions: {
        bannerType: isPastDue
          ? BANNER_TYPES.BILLING_PAST_DUE
          : BANNER_TYPES.BILLING_FAILED_INVOICE,
      },
      data: {
        variant: isPastDue ? 'critical' : 'warning',
        heading: 'Your account is past due',
        bodyText: `Pay your ${numberOfFailedInvoices} overdue invoice${
          isPastDue ? 's' : ''
        } to continue sending with Postscript.`,
        primaryAction: {
          text: 'Pay Now',
          onClick: showPayNowModal,
        },
        secondaryAction: {
          text: 'View Invoices',
          onClick: () => push('/billing/invoices?filter=FAILED'),
        },
      },
    });
  }

  function addFreeTrialBanner() {
    if (!freeTrial) return;

    const { expiresAt } = freeTrial;

    const daysRemaining = moment.utc(expiresAt).diff(moment(), 'days');
    const formattedExpirationDate = moment
      .utc(expiresAt)
      .format(LONG_DATE_FORMAT);

    addBanner({
      id: FREE_TRIAL_BANNER_ID,
      eventTrackingOptions: {
        bannerType: BANNER_TYPES.BILLING_FREE_TRIAL,
      },
      isDismissable: true,
      shouldPersistDismiss: true,
      data: {
        variant: 'guidance',
        heading: `${daysRemaining} ${
          daysRemaining > 1 ? 'days' : 'day'
        } left in your trial`,
        bodyText: `Your trial will end on ${formattedExpirationDate}.`,
      },
    });
  }

  async function buildUsageCreditBannerBodyText() {
    if (!usageCredits) return;

    const { amountTotal } = sumUsageCredits(activeUsageCredits);

    return (
      <>
        <strong>{formatDollars(estimatedUsageCreditRemaining / 100)}</strong>{' '}
        remaining of your{' '}
        {formatDollars(amountTotal / 100, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })}{' '}
        usage credit.
      </>
    );
  }

  async function updateUsageCreditBannerBodyText() {
    if (estimatedUsageCreditRemaining <= 0) {
      removeBanner(FREE_USAGE_CREDIT_BANNER_ID);
      removeBanner(FREE_USAGE_CREDIT_AND_FREE_TRIAL_BANNER_ID);

      if (isInFreeTrial) {
        addFreeTrialBanner();
      }

      return;
    }

    const bodyText = await buildUsageCreditBannerBodyText();

    const updateBannerById = (id: string) =>
      updateBanner({
        id,
        data: {
          bodyText,
        },
      });

    updateBannerById(FREE_USAGE_CREDIT_BANNER_ID);
    updateBannerById(FREE_USAGE_CREDIT_AND_FREE_TRIAL_BANNER_ID);
  }

  async function addFreeUsageCreditBanner() {
    if (estimatedUsageCreditRemaining <= 0) return;

    const bodyText = await buildUsageCreditBannerBodyText();

    addBanner({
      id: FREE_USAGE_CREDIT_BANNER_ID,
      eventTrackingOptions: {
        bannerType: BANNER_TYPES.BILLING_USAGE_CREDIT,
      },
      isDismissable: true,
      shouldPersistDismiss: true,
      data: {
        variant: 'guidance',
        heading: 'Usage credit',
        bodyText,
        primaryAction: {
          text: 'Visit Billing Overview',
          onClick: (event: React.SyntheticEvent) => {
            logButtonClickEvent(event, {
              banner_type: BANNER_TYPES.BILLING_USAGE_CREDIT,
            });
            push('/billing/overview');
          },
        },
      },
    });
  }

  async function addFreeUsageAndFreeTrialBanner() {
    if (!freeTrial) return;

    const { expiresAt: freeTrialExpiresAt } = freeTrial;
    const freeTrialDaysRemaining = moment
      .utc(freeTrialExpiresAt)
      .diff(moment(), 'days');

    if (estimatedUsageCreditRemaining <= 0) {
      return addFreeTrialBanner();
    }

    const bodyText = await buildUsageCreditBannerBodyText();

    addBanner({
      id: FREE_USAGE_CREDIT_AND_FREE_TRIAL_BANNER_ID,
      eventTrackingOptions: {
        bannerType: BANNER_TYPES.BILLING_FREE_TRIAL_AND_USAGE_CREDIT,
      },
      isDismissable: true,
      shouldPersistDismiss: true,
      data: {
        variant: 'guidance',
        heading: `${freeTrialDaysRemaining} ${
          freeTrialDaysRemaining > 1 ? 'days' : 'day'
        } left in your trial`,
        bodyText,
        primaryAction: {
          text: 'Visit Billing Overview',
          onClick: (event: React.SyntheticEvent) => {
            logButtonClickEvent(event, {
              banner_type: BANNER_TYPES.BILLING_FREE_TRIAL_AND_USAGE_CREDIT,
            });
            push('/billing/overview');
          },
        },
      },
    });
  }

  return null;
}
